<template>
  <div class="ivr-sessions-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader pageTitle="IVR Sessions" />
      <main class="page-content">

        <!-- Page Header -->
        <div class="page-header">
          <div class="header-content">
            <h1>IVR Sessions</h1>
            <p class="subtitle">IVR flow execution logs — nodes visited, DTMF inputs, and session variables.</p>
          </div>
          <button class="btn-secondary" @click="load">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg>
            Refresh
          </button>
        </div>

        <!-- Filters -->
        <div class="filters-bar">
          <div class="search-wrap">
            <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>
            <input v-model="filters.caller_id" class="search-input" placeholder="Search caller number..." @input="debouncedLoad" />
          </div>
          <select v-model="filters.status" class="filter-select" @change="load">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="transferred">Transferred</option>
            <option value="abandoned">Abandoned</option>
          </select>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
        </div>
        <div v-else-if="error" class="error-state">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
          {{ error }}
        </div>

        <div v-else>
          <div class="table-card">
            <table class="data-table" v-if="sessions.length">
              <thead>
                <tr>
                  <th>Caller</th>
                  <th>Flow</th>
                  <th>Status</th>
                  <th>Duration</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in sessions" :key="s.id" class="row-clickable" @click="openSession(s)">
                  <td>
                    <div class="caller-id">{{ s.caller_id }}</div>
                  </td>
                  <td class="muted-cell">{{ s.flow_name || '—' }}</td>
                  <td>
                    <span class="status-badge" :class="'status-' + s.status">{{ s.status }}</span>
                  </td>
                  <td class="muted-cell">{{ calcDuration(s.started_at, s.ended_at) }}</td>
                  <td class="muted-cell">{{ formatDate(s.started_at) }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="empty-state">
              <div class="empty-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
              </div>
              <h3>No call sessions found</h3>
              <p>Try adjusting your filters.</p>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination" v-if="totalPages > 1">
            <button class="page-btn" :disabled="page <= 1" @click="changePage(page - 1)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
              Previous
            </button>
            <span class="page-info">Page {{ page }} of {{ totalPages }}</span>
            <button class="page-btn" :disabled="page >= totalPages" @click="changePage(page + 1)">
              Next
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
            </button>
          </div>
        </div>

        <!-- Session Detail Slide-out -->
        <div v-if="selectedSession" class="slideout-overlay" @click.self="selectedSession = null">
          <div class="slideout">
            <div class="slideout-header">
              <div>
                <div class="slideout-title">Session #{{ selectedSession.id }}</div>
                <div class="slideout-caller">{{ selectedSession.caller_id }}</div>
              </div>
              <button class="slideout-close" @click="selectedSession = null">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>

            <div class="slideout-body" v-if="!sessionDetailLoading">
              <!-- Status & timing -->
              <div class="detail-row">
                <span class="status-badge" :class="'status-' + selectedSession.status">{{ selectedSession.status }}</span>
                <span class="muted-cell">{{ calcDuration(selectedSession.started_at, selectedSession.ended_at) }}</span>
              </div>

              <!-- Variables -->
              <div v-if="selectedSession.variables && Object.keys(selectedSession.variables).length" class="detail-section">
                <div class="section-title">Session Variables</div>
                <div class="vars-grid">
                  <div v-for="(val, key) in selectedSession.variables" :key="key" class="var-item">
                    <span class="var-key">{{ key }}</span>
                    <span class="var-val">{{ val }}</span>
                  </div>
                </div>
              </div>

              <!-- Node executions -->
              <div v-if="selectedSession.node_executions && selectedSession.node_executions.length" class="detail-section">
                <div class="section-title">Execution Timeline</div>
                <div class="timeline">
                  <div v-for="(exec, i) in selectedSession.node_executions" :key="i" class="timeline-item">
                    <div class="timeline-dot"></div>
                    <div class="timeline-content">
                      <div class="timeline-type">{{ exec.node_type }}</div>
                      <div class="timeline-meta">
                        <span v-if="exec.dtmf_received">Key: <strong>{{ exec.dtmf_received }}</strong></span>
                        <span v-if="exec.duration_ms">{{ exec.duration_ms }}ms</span>
                        <span>{{ formatTime(exec.executed_at) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="loading-state">
              <div class="spinner"></div>
            </div>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { IVRService } from '@/services/ivrService'

let debounceTimer = null

export default {
  name: 'IVRSessionsView',
  components: { Sidebar, TopHeader },
  data () {
    return {
      sessions: [],
      loading: true,
      error: null,
      page: 1,
      totalPages: 1,
      filters: { status: '', caller_id: '' },
      selectedSession: null,
      sessionDetailLoading: false
    }
  },
  async mounted () { await this.load() },
  methods: {
    async load () {
      this.loading = true
      this.error = null
      try {
        const params = { page: this.page }
        if (this.filters.status) params.status = this.filters.status
        if (this.filters.caller_id) params.caller_id = this.filters.caller_id
        const res = await IVRService.getSessions(params)
        this.sessions = res.data || []
        this.totalPages = res.last_page || 1
        this.page = res.current_page || 1
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },

    debouncedLoad () {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => this.load(), 400)
    },

    changePage (p) {
      this.page = p
      this.load()
    },

    async openSession (session) {
      this.selectedSession = { ...session }
      this.sessionDetailLoading = true
      try {
        const res = await IVRService.getSession(session.id)
        this.selectedSession = res.session
      } catch (e) {
        // keep the list-level data
      } finally {
        this.sessionDetailLoading = false
      }
    },

    calcDuration (start, end) {
      if (!start || !end) return '—'
      const ms = new Date(end) - new Date(start)
      const s = Math.floor(ms / 1000)
      const m = Math.floor(s / 60)
      const sec = s % 60
      return m > 0 ? `${m}m ${sec}s` : `${sec}s`
    },

    formatDate (str) {
      if (!str) return '—'
      return new Date(str).toLocaleString()
    },

    formatTime (str) {
      if (!str) return ''
      return new Date(str).toLocaleTimeString()
    }
  }
}
</script>

<style scoped>
.ivr-sessions-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
}
.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}
.page-content {
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
}

/* Page Header */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
}
.header-content h1 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px;
}
.subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

/* Filters */
.filters-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.search-wrap {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 320px;
}
.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 10px 14px 10px 38px;
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  font-size: 14px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.search-input:focus {
  outline: none;
  border-color: #3B82F6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}
.filter-select {
  padding: 10px 14px;
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  font-size: 14px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  cursor: pointer;
  transition: border-color 0.2s;
}
.filter-select:focus {
  outline: none;
  border-color: #3B82F6;
}

/* Loading / Error / Empty */
.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}
.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-primary);
  border-top-color: #3B82F6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.error-state {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 12px;
  padding: 16px 20px;
  color: #DC2626;
  font-size: 14px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 80px 20px;
  color: var(--text-secondary);
  text-align: center;
}
.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
}
.empty-state h3 { font-size: 18px; font-weight: 600; color: var(--text-primary); margin: 0; }
.empty-state p { font-size: 14px; margin: 0; }

/* Table */
.table-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  overflow: hidden;
}
.data-table { width: 100%; border-collapse: collapse; }
.data-table th {
  background: var(--bg-tertiary);
  padding: 12px 20px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid var(--border-primary);
}
.data-table td {
  padding: 14px 20px;
  border-bottom: 1px solid var(--border-primary);
  font-size: 14px;
  color: var(--text-primary);
}
.data-table tbody tr:last-child td { border-bottom: none; }
.row-clickable { cursor: pointer; }
.row-clickable:hover { background: var(--bg-tertiary); }

.caller-id { font-weight: 600; font-family: monospace; font-size: 14px; }
.muted-cell { color: var(--text-secondary); font-size: 13px; }

.status-badge {
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}
.status-active { background: rgba(59,130,246,0.1); color: #2563EB; }
.status-completed { background: #DCFCE7; color: #15803D; }
.status-failed { background: #FEE2E2; color: #DC2626; }
.status-transferred { background: rgba(139,92,246,0.1); color: #7C3AED; }
.status-abandoned { background: #FEF3C7; color: #D97706; }

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px;
}
.page-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  transition: all 0.2s;
}
.page-btn:hover:not(:disabled) { background: var(--bg-tertiary); border-color: var(--border-secondary); }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-info { font-size: 14px; color: var(--text-secondary); }

/* Buttons */
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  transition: all 0.2s;
}
.btn-secondary:hover { background: var(--bg-tertiary); }

/* Slide-out */
.slideout-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.3);
  z-index: 9998;
}
.slideout {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 440px;
  background: var(--bg-primary);
  border-left: 1px solid var(--border-primary);
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 32px rgba(0,0,0,0.12);
}
.slideout-header {
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
}
.slideout-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
}
.slideout-caller {
  font-size: 13px;
  color: var(--text-secondary);
  font-family: monospace;
  margin-top: 3px;
}
.slideout-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  transition: all 0.2s;
}
.slideout-close:hover { background: var(--bg-tertiary); color: var(--text-primary); }
.slideout-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.detail-section { margin-bottom: 24px; }
.section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
  margin-bottom: 12px;
}
.vars-grid { display: flex; flex-direction: column; gap: 8px; }
.var-item {
  display: flex;
  gap: 12px;
  font-size: 13px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 8px 12px;
}
.var-key {
  font-weight: 600;
  color: #3B82F6;
  font-family: monospace;
  min-width: 120px;
  flex-shrink: 0;
}
.var-val {
  color: var(--text-primary);
  font-family: monospace;
  word-break: break-all;
}

/* Timeline */
.timeline { display: flex; flex-direction: column; }
.timeline-item {
  display: flex;
  gap: 14px;
  padding-bottom: 14px;
  position: relative;
}
.timeline-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 5px;
  top: 14px;
  bottom: 0;
  width: 2px;
  background: var(--border-primary);
}
.timeline-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #3B82F6;
  flex-shrink: 0;
  margin-top: 3px;
}
.timeline-content { flex: 1; }
.timeline-type {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}
.timeline-meta {
  display: flex;
  gap: 10px;
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 3px;
}
</style>
